import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Settings, Check, AlertCircle, Plus, Trash2, RefreshCw, ShieldCheck, Clock, Star, Server, KeyRound, ChevronDown, ChevronUp, Info, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('authToken')}` });

const EmailSettings = () => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Add-form state
  const [form, setForm] = useState({
    email: '',
    display_name: '',
    smtp: { host: '', port: 465, secure: true, username: '', password: '' },
    imap: { host: '', port: 993, secure: true, username: '', password: '' },
    syncImapWithSmtp: true,
    set_primary: false
  });
  const [providerHelp, setProviderHelp] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/list`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAccounts(data.data);
    } catch (err) { console.error('Fetch accounts failed:', err); }
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 6000);
  };

  // When user finishes typing email, auto-pull provider defaults
  const handleEmailBlur = async () => {
    if (!form.email || !form.email.includes('@')) return;
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/provider-defaults?email=${encodeURIComponent(form.email)}`, {
        credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success && data.data) {
        const d = data.data;
        setForm(prev => ({
          ...prev,
          smtp: { ...prev.smtp, host: d.smtp.host, port: d.smtp.port, secure: d.smtp.secure, username: prev.smtp.username || form.email },
          imap: { ...prev.imap, host: d.imap.host, port: d.imap.port, secure: d.imap.secure, username: prev.imap.username || form.email }
        }));
        setProviderHelp(d.helpText && d.helpUrl ? { text: d.helpText, url: d.helpUrl } : null);
      } else {
        // Unknown provider — set defaults to email but leave hosts blank
        setForm(prev => ({
          ...prev,
          smtp: { ...prev.smtp, username: prev.smtp.username || form.email },
          imap: { ...prev.imap, username: prev.imap.username || form.email }
        }));
        setProviderHelp(null);
      }
    } catch (err) { console.warn('Provider defaults fetch failed:', err); }
  };

  // Mirror SMTP password/username into IMAP when "use same creds" is checked
  const updateSmtp = (patch) => {
    setForm(prev => {
      const smtp = { ...prev.smtp, ...patch };
      const imap = prev.syncImapWithSmtp
        ? { ...prev.imap, username: smtp.username, password: smtp.password }
        : prev.imap;
      return { ...prev, smtp, imap };
    });
    setTestResult(null); // invalidate test on field change
  };
  const updateImap = (patch) => {
    setForm(prev => ({ ...prev, imap: { ...prev.imap, ...patch } }));
    setTestResult(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ smtp: form.smtp, imap: form.imap })
      });
      const data = await res.json();
      if (data.success) setTestResult(data.data);
      else showMessage('error', data.message || 'Test failed');
    } catch (err) { showMessage('error', 'Test request failed: ' + err.message); }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!form.email.includes('@')) return showMessage('error', 'Valid email required');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/add-smtp`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        showMessage('success', data.message || 'Account saved');
        setIsAddOpen(false);
        setForm({
          email: '', display_name: '',
          smtp: { host: '', port: 465, secure: true, username: '', password: '' },
          imap: { host: '', port: 993, secure: true, username: '', password: '' },
          syncImapWithSmtp: true, set_primary: false
        });
        setTestResult(null);
        fetchAccounts();
      } else {
        showMessage('error', data.message || 'Save failed');
        if (data.data) setTestResult(data.data); // server tested before save and failed
      }
    } catch (err) { showMessage('error', 'Save failed: ' + err.message); }
    setSaving(false);
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Remove ${item.email}?`)) return;
    const url = item.source === 'smtp'
      ? `${API_URL}/api/senderEmail/remove-smtp/${item.id}`
      : `${API_URL}/api/senderEmail/remove/${item.id}`;
    try {
      const res = await fetch(url, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) { showMessage('success', 'Removed'); fetchAccounts(); }
      else showMessage('error', data.message || 'Remove failed');
    } catch (err) { showMessage('error', err.message); }
  };

  const handleSetPrimary = async (item) => {
    if (item.source !== 'smtp') return;
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/set-primary-smtp/${item.id}`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) { showMessage('success', 'Primary updated'); fetchAccounts(); }
      else showMessage('error', data.message || 'Update failed');
    } catch (err) { showMessage('error', err.message); }
  };

  const [checkingReplies, setCheckingReplies] = useState(null); // account id currently being scanned
  const handleCheckReplies = async (item) => {
    if (item.source !== 'smtp') return;
    setCheckingReplies(item.id);
    try {
      const res = await fetch(`${API_URL}/api/email-fetch/reply-tracker/run-now/${item.id}`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        const n = data.data?.totalNewReplies || 0;
        showMessage(n > 0 ? 'success' : 'success', data.message || (n > 0 ? `Found ${n} new` : 'No new replies'));
      } else {
        showMessage('error', data.message || 'Reply check failed');
      }
    } catch (err) { showMessage('error', err.message); }
    setCheckingReplies(null);
  };

  const handleRetest = async (item) => {
    if (item.source !== 'smtp') return;
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/test-existing/${item.id}`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success && data.data.ok) showMessage('success', 'Connection OK');
      else if (data.success) showMessage('error', `SMTP: ${data.data.smtp.error || 'ok'} | IMAP: ${data.data.imap.error || 'ok'}`);
      else showMessage('error', data.message || 'Re-test failed');
      fetchAccounts();
    } catch (err) { showMessage('error', err.message); }
  };

  const smtpCount = accounts.filter(a => a.source === 'smtp').length;
  const verifiedCount = accounts.filter(a => a.is_verified).length;

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                Email Settings
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                Connect your own email account using SMTP + IMAP. Sending uses your real address (better deliverability) and you'll be able to read your Inbox + Sent folder inside the app.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 shadow-sm">
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {verifiedCount} / {accounts.length} verified
                </span>
              </div>
              <button onClick={fetchAccounts} className="p-2 text-gray-500 dark:text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg" title="Refresh">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Banner: prompt SES users to upgrade */}
      {smtpCount === 0 && accounts.some(a => a.source === 'ses') && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Upgrade to SMTP + IMAP for inbox access</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              You're currently using the legacy SES verification. Add an SMTP+IMAP account below to view your inbox and sent folder inside the app.
            </p>
          </div>
        </div>
      )}

      {/* Account list */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-2 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Your Email Accounts</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
              <span className="ml-3 text-gray-500 dark:text-slate-400 text-sm">Loading...</span>
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <div className="p-3 bg-gray-100 dark:bg-slate-800 rounded-full mb-3">
                <Mail className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 dark:text-slate-400 font-medium">No email accounts yet</p>
              <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">Click "Add account" below to connect your first email</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-800">
              {accounts.map(item => (
                <div key={`${item.source}-${item.id}`} className="flex items-center justify-between px-6 py-4 gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${item.is_verified ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                      {item.is_verified
                        ? <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                        : <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-800 dark:text-slate-100 truncate">{item.email}</span>
                        {item.is_primary && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full font-medium">Primary</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.source === 'smtp' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>
                          {item.source === 'smtp' ? 'SMTP+IMAP' : 'Legacy SES'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_verified ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>
                          {item.is_verified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      {item.last_test_error && (
                        <p className="text-xs text-red-500 mt-0.5 truncate" title={item.last_test_error}>
                          Last test: {item.last_test_error}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.source === 'smtp' && !item.is_primary && (
                      <button onClick={() => handleSetPrimary(item)} title="Set as primary"
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    {item.source === 'smtp' && (
                      <button onClick={() => handleCheckReplies(item)} title="Check for replies now"
                        disabled={checkingReplies === item.id}
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg disabled:opacity-50">
                        <Bell className={`w-4 h-4 ${checkingReplies === item.id ? 'animate-pulse' : ''}`} />
                      </button>
                    )}
                    {item.source === 'smtp' && (
                      <button onClick={() => handleRetest(item)} title="Re-test connection"
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    {(item.source === 'smtp' || !item.is_primary) && (
                      <button onClick={() => handleRemove(item)} title="Remove"
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-xl border flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
            : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="font-medium text-sm">{message.text}</span>
        </div>
      )}

      {/* Add account (collapsible) */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <button
            onClick={() => setIsAddOpen(!isAddOpen)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Add email account</h2>
            </div>
            {isAddOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
          </button>

          {isAddOpen && (
            <div className="px-6 pb-6 border-t border-gray-100 dark:border-slate-800 pt-4 space-y-5">
              {/* Email + display name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Email address *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => { setForm({ ...form, email: e.target.value }); setTestResult(null); }}
                    onBlur={handleEmailBlur}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Display name (optional)</label>
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={e => setForm({ ...form, display_name: e.target.value })}
                    placeholder="Your Name"
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
              </div>

              {providerHelp && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {providerHelp.text}{' '}
                    <a href={providerHelp.url} target="_blank" rel="noopener noreferrer" className="underline font-medium">Learn how →</a>
                  </p>
                </div>
              )}

              {/* SMTP block */}
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
                  <Server className="w-4 h-4" /> SMTP (sending)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Host</label>
                    <input type="text" value={form.smtp.host} onChange={e => updateSmtp({ host: e.target.value })} placeholder="smtp.gmail.com" className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Port</label>
                    <input type="number" value={form.smtp.port} onChange={e => updateSmtp({ port: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Username</label>
                    <input type="text" value={form.smtp.username} onChange={e => updateSmtp({ username: e.target.value })} placeholder="usually your email" className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Password (or app password)</label>
                    <input type="password" value={form.smtp.password} onChange={e => updateSmtp({ password: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                  <input type="checkbox" checked={form.smtp.secure} onChange={e => updateSmtp({ secure: e.target.checked })} />
                  Use SSL/TLS (uncheck for STARTTLS on port 587)
                </label>
              </div>

              {/* Use-same toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={form.syncImapWithSmtp}
                  onChange={e => {
                    const checked = e.target.checked;
                    setForm(prev => ({
                      ...prev,
                      syncImapWithSmtp: checked,
                      imap: checked ? { ...prev.imap, username: prev.smtp.username, password: prev.smtp.password } : prev.imap
                    }));
                    setTestResult(null);
                  }}
                />
                Use same username + password for IMAP (most providers)
              </label>

              {/* IMAP block */}
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
                  <KeyRound className="w-4 h-4" /> IMAP (reading inbox / sent)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Host</label>
                    <input type="text" value={form.imap.host} onChange={e => updateImap({ host: e.target.value })} placeholder="imap.gmail.com" className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Port</label>
                    <input type="number" value={form.imap.port} onChange={e => updateImap({ port: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                  </div>
                </div>
                {!form.syncImapWithSmtp && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Username</label>
                      <input type="text" value={form.imap.username} onChange={e => updateImap({ username: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Password</label>
                      <input type="password" value={form.imap.password} onChange={e => updateImap({ password: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                  <input type="checkbox" checked={form.imap.secure} onChange={e => updateImap({ secure: e.target.checked })} />
                  Use SSL/TLS
                </label>
              </div>

              {/* Test result */}
              {testResult && (
                <div className={`rounded-lg p-3 border ${testResult.ok ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                  <div className="flex items-start gap-2 text-sm">
                    {testResult.ok ? <Check className="w-4 h-4 text-green-600 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />}
                    <div className="flex-1">
                      <div className={testResult.smtp.ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}>
                        SMTP: {testResult.smtp.ok ? 'OK' : `Failed — ${testResult.smtp.error}`}
                      </div>
                      <div className={testResult.imap.ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}>
                        IMAP: {testResult.imap.ok ? 'OK' : `Failed — ${testResult.imap.error}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Set primary toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                <input type="checkbox" checked={form.set_primary} onChange={e => setForm({ ...form, set_primary: e.target.checked })} />
                Make this my primary sender
              </label>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleTest}
                  disabled={testing || !form.email || !form.smtp.host || !form.imap.host}
                  className="flex-1 px-4 py-2.5 border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:border-gray-300 disabled:text-gray-400 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  {testing ? <><div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" /> Testing…</> : <>Test connection</>}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !testResult?.ok}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                  title={!testResult?.ok ? 'Run a successful test first' : 'Save'}
                >
                  {saving ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving…</> : <>Save account</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help card */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">How it works</h3>
              <ul className="text-gray-600 dark:text-slate-400 leading-relaxed space-y-1 text-sm">
                <li>1. <strong>Enter your email address.</strong> We'll automatically fill in the right server settings for both personal emails and work emails — you don't need to look up host or port numbers.</li>
                <li>2. <strong>Enter your email password.</strong> For personal emails, most providers require an <strong>app password</strong> (generated inside your email account's security settings) instead of your regular login password. For work emails, use the password your IT team gave you.</li>
                <li>3. <strong>Click Test Connection.</strong> We'll check two things in the background: that we can send email from your address (SMTP) and receive/read email for your address (IMAP). Both must pass before you can save.</li>
                <li>4. <strong>Click Save.</strong> Your account is now verified and linked. From here on, every email you send goes out from your real address, and all your sent messages plus incoming replies appear inside the <strong>Inbox</strong> section in the sidebar.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
